import { ChangeDetectionStrategy, Component, Input, ViewChild } from "@angular/core";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { TeamTypeCode } from "@common/ADAPT.Common.Model/organisation/team-type-code";
import { TeamTypeConfiguration } from "@common/ADAPT.Common.Model/organisation/team-type-configuration";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { IBreezeEntity } from "@common/lib/data/breeze-entity.interface";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { ArrayUtilities } from "@common/lib/utilities/array-utilities";
import { SortUtilities } from "@common/lib/utilities/sort-utilities";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { IColourPickerPresetGroups } from "@common/ux/colour-picker/colour-picker.component";
import { IDxListItemReorderedEvent, IDxListSelectionChangedEvent } from "@common/ux/dx.types";
import { ConfigureFeatureBase } from "@org-common/lib/configuration/base-components/configure-feature-base";
import { IConfigItem } from "@org-common/lib/configuration/configuration.interfaces";
import { ConfigurationService } from "@org-common/lib/configuration/configuration.service";
import { CommonTeamsService } from "@org-common/lib/teams/common-teams.service";
import { ContentReadyEvent } from "devextreme/ui/list";
import { DxTextBoxComponent } from "devextreme-angular/ui/text-box";
import { lastValueFrom, merge } from "rxjs";
import { switchMap, tap } from "rxjs/operators";

@Component({
    selector: "adapt-configure-teams",
    templateUrl: "./configure-teams.component.html",
    styleUrl: "./configure-teams.component.scss",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class ConfigureTeamsComponent extends ConfigureFeatureBase {
    public readonly TeamTypeCode = TeamTypeCode;

    @Input() public configItem?: IConfigItem;

    @ViewChild("focus") public focusElement?: DxTextBoxComponent;

    public teamTypeConfigurations: TeamTypeConfiguration[] = [];
    public selectedConfig?: TeamTypeConfiguration;
    public dummyTeam?: Team;

    public iconColourPresetGroups?: IColourPickerPresetGroups;
    public backgroundColourPresetGroups?: IColourPickerPresetGroups;

    public constructor(
        configurationService: ConfigurationService,
        rxjsBreezeService: RxjsBreezeService,
        private commonDataService: CommonDataService,
        private dialogService: AdaptCommonDialogService,
        private teamsService: CommonTeamsService,
    ) {
        super(configurationService);

        merge(
            rxjsBreezeService.entityTypeChanged(TeamTypeConfiguration),
            rxjsBreezeService.entityTypeChanged(Team),
        ).pipe(
            switchMap(() => this.initialiseData()),
            this.takeUntilDestroyed(),
        ).subscribe();
    }

    public async initialiseData() {
        this.teamTypeConfigurations = await this.teamsService.promiseToGetTeamTypeConfigurations();
        if (this.teamTypeConfigurations.length > 0 && !this.selectedConfig) {
            this.updateSelectedTeamTypeConfiguration(this.teamTypeConfigurations[0]);
        }

        this.backgroundColourPresetGroups = await this.teamsService.promiseToGetUsedTeamBackgroundColourPresetGroup();
        this.iconColourPresetGroups = await this.teamsService.promiseToGetUsedTeamIconColourPresetGroup();
    }

    public onContentReady(e: ContentReadyEvent) {
        e.component!.selectItem(this.selectedConfig);
    }

    public async onCancel() {
        if (this.selectedConfig?.entityAspect.entityState.isDetached()) {
            this.selectedConfig = undefined;
        }

        await this.initialiseData();
    }

    public async onSelectionChanged(e: IDxListSelectionChangedEvent<TeamTypeConfiguration>) {
        this.updateSelectedTeamTypeConfiguration(e.addedItems.length ? e.addedItems[0] : undefined);
    }

    public updateDummyTeam() {
        if (!this.selectedConfig) {
            this.dummyTeam = undefined;
            return;
        }

        this.dummyTeam = new Team();
        this.dummyTeam.name = this.selectedConfig.name;
        this.dummyTeam.teamTypeConfiguration = this.selectedConfig;
    }

    @Autobind
    public addTeamType() {
        return this.teamsService.createTeamTypeConfiguration().pipe(
            switchMap((teamTypeConfiguration) => this.initialiseData()
                .then(() => teamTypeConfiguration)),
            tap((teamTypeConfiguration) => {
                this.updateSelectedTeamTypeConfiguration(teamTypeConfiguration);
                this.focusElement?.instance.focus();
            }),
        );
    }

    @Autobind
    public async deleteTeamTypeConfiguration(teamTypeConfiguration: TeamTypeConfiguration) {
        const standardTeamConfig = this.teamTypeConfigurations.find((t) => t.code === TeamTypeCode.Team);
        if (!standardTeamConfig) {
            throw new Error("Team reference data not found!");
        }

        const affectedTeams = await lastValueFrom(this.teamsService.getTeamsByTeamTypeConfiguration(teamTypeConfiguration));
        let message = `<p>Are you sure you wish to delete the <b>${teamTypeConfiguration.name}</b> group type?</p>`;
        if (affectedTeams.length > 0) {
            message += `<p>The following <b>${teamTypeConfiguration.name}s</b> will become <b>${standardTeamConfig.name}s</b> instead:</p>`;
            message += `<ul>`;
            for (const team of affectedTeams) {
                message += `<li>${team.name}</li>`;
            }
            message += `</ul>`;
        } else {
            message += `<p>The <b>${teamTypeConfiguration.name}</b> group type is not used by any groups.</p>`;
        }

        const confirm = await lastValueFrom(this.dialogService.openConfirmationDialogWithBoolean({
            title: "Delete group type",
            message,
            confirmButtonPreset: "deleteAndSave",
            checkboxMessage: "Confirm that you wish to delete this group type",
        }), { defaultValue: false });
        if (!confirm) {
            return;
        }

        for (const team of affectedTeams) {
            team.teamTypeConfiguration = standardTeamConfig;
        }

        const entities: IBreezeEntity[] = [teamTypeConfiguration, ...affectedTeams, ...this.teamTypeConfigurations];

        // Pre-emptively clear the selection before removing the item from the array.
        // DevExtreme dx-list fires onSelectionChanged synchronously when the selected
        // item is removed from [items], which would change selectedConfig from an object
        // to undefined mid-change-detection-cycle, triggering ExpressionChangedAfterItHasBeenCheckedError.
        if (this.selectedConfig === teamTypeConfiguration) {
            this.selectedConfig = undefined;
            this.dummyTeam = undefined;
        }

        try {
            await lastValueFrom(this.commonDataService.remove(teamTypeConfiguration));
            ArrayUtilities.removeElementFromArray(teamTypeConfiguration, this.teamTypeConfigurations);
            SortUtilities.updateIntegerSortedArrayAfterItemRemoval(this.teamTypeConfigurations, "ordinal", teamTypeConfiguration.ordinal);
            await lastValueFrom(this.commonDataService.saveEntities(ArrayUtilities.distinct(entities)));
        } catch (e: any) {
            await lastValueFrom(this.dialogService.handleSaveFailure(entities, e), { defaultValue: undefined });
        }

        // update state
        await this.initialiseData();
    }

    public onItemReordered(_e: IDxListItemReorderedEvent<TeamTypeConfiguration>) {
        SortUtilities.sequenceNumberFieldInArray(this.teamTypeConfigurations, "ordinal");
    }

    private updateSelectedTeamTypeConfiguration(teamTypeConfiguration?: TeamTypeConfiguration) {
        this.selectedConfig = teamTypeConfiguration;
        this.updateDummyTeam();
    }
}
